import { useState } from "react";
import { FileText, FileSpreadsheet, Image as ImageIcon, Download, Eye, X, Paperclip } from "lucide-react";
import FileViewer from "@/components/FileViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const DOC_ACCEPT = "image/*,application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const DOC_ACCEPT_NO_IMG = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const DOC_ACCEPT_EXCEL = `${DOC_ACCEPT},.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
const MAX = 10 * 1024 * 1024;

export const tokenQs = () => {
  const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  return t ? `auth=${encodeURIComponent(t)}` : "";
};
export const attHref = (base, attId, download) => {
  const qs = [tokenQs(), download ? "download=1" : ""].filter(Boolean).join("&");
  return `${BACKEND}/api/${base}/attachments/${attId}${qs ? `?${qs}` : ""}`;
};
export const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
export const checkFiles = (list, onError) => {
  const ok = [];
  Array.from(list || []).forEach((f) => {
    if (f.size > MAX) onError?.(`${f.name} dépasse 10 Mo`);
    else ok.push(f);
  });
  return ok;
};

// base = "consignes/<id>" ou "reclamations/<id>"
export function DocAttachments({ base, items = [], onRemove, testPrefix = "att", extra }) {
  const [view, setView] = useState(null);
  if (!items.length) return null;
  const open = (a) => setView({ name: a.name, content_type: a.content_type, url: attHref(base, a.att_id, false), downloadUrl: attHref(base, a.att_id, true) });
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testPrefix}-list`}>
      <FileViewer file={view} onClose={() => setView(null)} />
      {items.map((a) => {
        const isImg = (a.content_type || "").startsWith("image/");
        const isPdf = a.content_type === "application/pdf";
        const isXls = /\.xlsx?$/i.test(a.name || "");
        const Icon = isImg ? ImageIcon : isXls ? FileSpreadsheet : FileText;
        return (
          <div key={a.att_id} data-testid={`${testPrefix}-${a.att_id}`} className="flex items-center gap-2 text-xs rounded-md border border-white/10 bg-zinc-900 px-2.5 py-1.5">
            <Icon className={`w-4 h-4 shrink-0 ${isPdf ? "text-[#FF3B30]" : isImg || isXls ? "text-emerald-500" : "text-[#007AFF]"}`} />
            <button type="button" onClick={() => open(a)} title="Aperçu" data-testid={`${testPrefix}-open-${a.att_id}`} className="truncate text-left text-zinc-200 hover:text-[#007AFF] hover:underline">{a.name}</button>
            {a.size ? <span className="text-zinc-500 shrink-0">{fmtSize(a.size)}</span> : null}
            <span className="ml-auto flex items-center gap-1 shrink-0">
              {extra?.(a)}
              <button type="button" onClick={() => open(a)} title="Aperçu" data-testid={`${testPrefix}-preview-${a.att_id}`} className="p-1 text-zinc-400 hover:text-[#007AFF]"><Eye className="w-3.5 h-3.5" /></button>
              <a href={attHref(base, a.att_id, true)} title="Télécharger" data-testid={`${testPrefix}-download-${a.att_id}`} className="p-1 text-zinc-400 hover:text-[#007AFF]"><Download className="w-3.5 h-3.5" /></a>
              {onRemove && <button type="button" onClick={() => onRemove(a)} title="Retirer" data-testid={`${testPrefix}-remove-${a.att_id}`} className="p-1 text-zinc-500 hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function FilePickerField({ label, accept, files, onAdd, onRemove, testId }) {
  return (
    <div>
      <p className="text-zinc-300 text-sm mb-1">{label}</p>
      <label className="flex items-center gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900 px-3 py-2.5 cursor-pointer hover:border-[#007AFF]" data-testid={`${testId}-label`}>
        <Paperclip className="w-4 h-4 text-zinc-400" /><span className="text-sm text-zinc-400">Ajouter des fichiers</span>
        <input type="file" multiple accept={accept} className="hidden" onChange={(e) => { const picked = Array.from(e.target.files || []); e.target.value = ""; onAdd(picked); }} data-testid={testId} />
      </label>
      {files.map((file, i) => (
        <div key={`${file.name}-${i}`} className="mt-1 flex items-center justify-between text-xs text-zinc-300 bg-zinc-900 rounded px-2 py-1" data-testid={`${testId}-pending-${i}`}>
          <span className="truncate">{file.name} <span className="text-zinc-500">· {fmtSize(file.size)}</span></span>
          <button type="button" onClick={() => onRemove(i)} className="text-zinc-500 hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}
